import type { ReactNode } from 'react';
import type { Position } from '../../types/problems';

/**
 * Places the hand relative to the vulnerability board according to the seat holding it
 * (client correction, 2026-07-22): it is the HAND that follows the seat, so the arrangement
 * reads as a picture of where the learner is sitting.
 *
 * The hand sits on the board's compass side for its own seat:
 *
 *   North — hand above the board     South — hand below the board
 *   East  — hand right of the board  West  — hand left of the board
 *
 * The East/West pair was inverted until 2026-07-23. North and South always followed the compass
 * (North above, South below), so the two horizontal cases were the odd ones out, and the board
 * read as a mirror of the table rather than a picture of it. Feature 016's seat-relative rule for
 * the review surfaces had it right all along; this now agrees with it, so both arrangements state
 * the same thing.
 *
 * The hand is always the FIRST child, so `-reverse` is what moves the board to the far side.
 * That keeps the reading order identical in all four cases — a screen reader always meets the
 * learner's own hand before the board, whatever the visual arrangement.
 */

export interface SeatBoardLayoutProps {
  /** Seat holding the hand. An unusable value falls back to the South arrangement. */
  seat: string | null | undefined;
  hand: ReactNode;
  board: ReactNode;
  /**
   * Label shown directly above the hand (e.g. "You hold — West"). It rides with the hand
   * rather than sitting at the top of the panel, so it stays over the cards wherever the seat
   * puts them — below the board for South, right of it for East, and so on.
   */
  handLabel?: ReactNode;
}

// The hand is the first child: `flex-row` puts it LEFT of the board, `flex-row-reverse` RIGHT;
// `flex-col` puts it ABOVE, `flex-col-reverse` BELOW.
const STACK: Record<Position, string> = {
  N: 'flex-col',
  S: 'flex-col-reverse',
  E: 'flex-row-reverse',
  W: 'flex-row',
};

export function SeatBoardLayout({ seat, hand, board, handLabel }: SeatBoardLayoutProps) {
  const position: Position =
    seat === 'N' || seat === 'E' || seat === 'W' ? (seat as Position) : 'S';

  const horizontal = position === 'E' || position === 'W';

  return (
    // `items-center` centres the cross axis in both arrangements: it stacks the hand and board
    // over/under each other for North/South (so a vertical seat reads as centred, not shoved to
    // one side like an East/West hand), and keeps them vertically aligned for East/West.
    // `seat-board` scopes the portrait-phone horizontal-suits CSS (index.css) — the
    // pre-023 /problems phone treatment, where an N/S hand lays its four suits out in a
    // row so the stacked panel stays short.
    <div
      className={`seat-board flex items-center gap-3 xnarrow:gap-1.5 ${STACK[position]}`}
      data-testid="seat-board-layout"
      data-seat={position}
    >
      <div className={`min-w-0 flex-1 ${horizontal ? '' : 'flex flex-col items-center'}`}>
        {/* The heading matches PanelHeading (TableFrame) exactly — 12px floor,
            sentence case on phones. See its doc comment for why. */}
        {handLabel && (
          <h2 className="mb-2.5 text-xs font-semibold uppercase tracking-[0.09em] text-ink-soft narrow:normal-case narrow:tracking-normal padswap:mb-1.5 xnarrow:mb-1">
            {handLabel}
          </h2>
        )}
        {hand}
      </div>
      <div className={horizontal ? 'shrink-0' : ''}>{board}</div>
    </div>
  );
}
